import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../shared/Seo';

const ABOUT_SECTIONS = [
  {
    title: 'What Is Gachapon?',
    body:
      'Gachapon (also called gashapon or capsule toys) are iconic Japanese vending-machine collectibles known for their high-quality miniatures, themed sets, and surprise-based fun.',
    subBody:
      'Top manufacturers like Bandai, Takara Tomy, and Stasto produce highly detailed figures that fans around the world collect, display, and trade.',
    footnote:
      'PonPon preserves the heart of this culture by recreating the same excitement in a digital format, while ensuring all items remain authentic, physical collectibles.',
  },
  {
    title: 'How PonPon Work',
    body: 'PonPon does not use physical gachapon machines.',
    subBody:
      'Instead, we use a proprietary fairness algorithm that accurately models the behavior of real Japanese machines:',
    bullets: [
      'True-to-original rarity distributions',
      'Unbiased, transparent randomness',
      'Accurate modeling of full sets',
      'Clear lineup and odds displayed for every series',
    ],
    footnote:
      'This system allows users to enjoy the same excitement as spinning real gachapon machines instantly, online.\n\nAlthough spins are digital, every result corresponds to an actual physical toy that we pack and ship to your home.',
  },
  {
    title: 'What We Offer',
    bullets: [
      'Authentic capsule toys sourced directly from Japan',
      'A digital spinning experience that mimics real gachapon randomness',
      'A personal Locker to store up to 8 items before shipping',
      'Fast and reliable U.S. domestic shipping',
      'Full series lineups for easier collecting and set completion',
    ],
    footnote:
      'PonPon combines the convenience of online play with the authenticity of real collectibles.',
  },
  {
    title: 'Our Story',
    body:
      'As lifelong collectors and fans of Japanese pop culture, we noticed a gap:',
    subBody:
      'Gachapon was incredibly popular in Japan, but hard to access in the U.S., often involving slow overseas shipping, high fees, inconsistent inventory, or uncertainty around authenticity.',
    footnote:
      'So we set out to build a platform that solves these problems.\n\nPonPon simplifies the entire experience with:\n\n• Direct Japan sourcing\n• Fair algorithmic spins\n• Instant online reveals\n• Convenient domestic fulfillment\n\nOur goal is to make gachapon accessible, fun, and trustworthy for collectors across the country.',
  },
  {
    title: 'Our Mission',
    body:
      'To bring the joy, surprise, and creativity of Japanese gachapon culture to everyone. One spin at a time.',
  },
  {
    title: 'Who We Are',
    body:
      'PonPon is a small U.S.-based startup built by two collectors who love gachapon and Japanese pop culture.',
    subBody:
      'We design and develop our platform in-house, manage our own inventory, and handpack every shipment with care.',
    footnote:
      "We're independent, passionate, and committed to building the most trusted destination for online gachapon.",
  },
];

const AboutPage = () => {
  useEffect(() => {
    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div className="flex flex-col px-4 pt-4 pb-8 sm:px-0 sm:pt-0 sm:pb-16">
      <Seo
        title="About | PonPon!"
        description="Learn about PonPon, the online gachapon platform bringing authentic Japanese capsule toys to the U.S. Discover our story, mission, and how we work."
        canonical="https://ponpon.app/about"
        type="article"
      />
      <header className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">About PonPon</h1>
      </header>

      <div className="mb-6 space-y-4">
        <p className="text-sm leading-relaxed text-ponpon-navy/75">
          PonPon is an online interactive gachapon bringing Japanese capsule toy series to collectors across the United States. Each spin adds one item from the selected series to your locker. All items are stored in our U.S. warehouse and shipped directly to your home.
        </p>

        <p className="text-sm leading-relaxed text-ponpon-navy/75">
          We built PonPon so fans can enjoy the thrill of gachapon, anytime, anywhere, without the long waits or limited availability of local gachapon stores.
        </p>
      </div>

      <div className="space-y-6">
        {ABOUT_SECTIONS.map(({ title, body, subBody, bullets, footnote }) => (
          <div key={title} className="space-y-2">
            <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">{title}</h2>
            {body ? (
              <p className="text-sm leading-relaxed text-ponpon-navy/75">{body}</p>
            ) : null}
            {subBody ? (
              <p className="text-sm leading-relaxed text-ponpon-navy/75">{subBody}</p>
            ) : null}
            {Array.isArray(bullets) ? (
              <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
                {bullets.map((item, index) => (
                  <li key={`${title}-${index}`} className="whitespace-pre-line">
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
            {footnote ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-ponpon-navy/75">
                {footnote}
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">Want to Learn More?</h2>
          <p className="text-sm leading-relaxed text-ponpon-navy/75">
            Check out our full{' '}
            <Link
              to="/faq"
              className="font-semibold text-ponpon-coral underline underline-offset-2 transition hover:text-[#E23A32]"
            >
              FAQ
            </Link>{' '}
            to learn about shipping, Locker features, fairness, and collecting tips.
          </p>
        </div>
        <div className="flex justify-end">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
          >
            <span aria-hidden="true">←</span>
            Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;

